import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import BlackoutScopeTag from "ui/Blackout/BlackoutScopeTag"
import styles from "./ResourceConflictCard.module.css"

/**
 * A resource that cannot serve the day's bookings. Informational only: there is no action to
 * take from here yet.
 *
 * The feed keys a conflict the same way it keys a blackout — `type` is the scope — so the card
 * carries the same scope pill the blackout cards do, and the guide reads "crew" or "equipment"
 * off the row without having to infer it from the resource's name.
 */
const ResourceConflictCard = ({ conflict }) => {
  const { t } = useTranslation()

  // `name` is what the feed sends; `label`/`detail` are the contract's naming. Both are read
  // until the shape is settled, so the card is never blank whichever arrives.
  const title = conflict.label || conflict.name || t("calendar.blackout.conflict_generic", { defaultValue: "Resource conflict" })
  const affected =
    conflict.count === 1
      ? t("calendar.blackout.conflict_bookings_one", { defaultValue: "1 booking affected" })
      : t("calendar.blackout.conflict_bookings", { defaultValue: "{{count}} bookings affected", count: conflict.count })

  return (
    <article className={styles.card}>
      <Flex align="center" gap="var(--spacing-xs)" wrap>
        <Icon name="AlertTriangle" width={20} height={20} color="var(--color-warning)" />
        <span className={styles.title}>{title}</span>
        {conflict.type && <BlackoutScopeTag scope={conflict.type} tone="solid" className={styles.scopeTag} />}
      </Flex>
      {conflict.detail && <span className={styles.detail}>{conflict.detail}</span>}
      {Boolean(conflict.count) && <span className={styles.detail}>{affected}</span>}
    </article>
  )
}

ResourceConflictCard.propTypes = {
  conflict: PropTypes.shape({
    /** The scope the conflict belongs to — same vocabulary as a blackout's scope. */
    type: PropTypes.string,
    /** The contract's naming. */
    label: PropTypes.string,
    detail: PropTypes.string,
    /** What the feed actually sends today. */
    name: PropTypes.string,
    count: PropTypes.number
  }).isRequired
}

export default ResourceConflictCard
